<template>
  <div data-test-id="activateContent" class="section">
    <h2>{{ t('headers.content.activate') }}</h2>
    <p class="section-content" v-html="instructions" />
    <p class="section-content">
      <v-form :fast-fail="false" @submit.prevent ref="activationForm">
        <PrimaryTextField
          ref="codeField"
          maxlength="39"
          v-model="_activationCode"
          @keyup="validateForm()"
          :rules="RulesManager.getRules([Rule.Required, Rule.ValidActivationCode])"
          :label="t('labels.activationCode')"
          required
        />
        <span class="section-content" v-if="errorMessage" style="color: red; font-size: 0.8rem">
          {{ errorMessage }}
        </span>
      </v-form>
    </p>
    <ButtonPrimary
      :loading="isValidatingCode"
      :disabled="!isFormValid || isValidatingCode"
      :text="t('buttons.activate')"
      @click="activate"
    />
  </div>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { useRulesManager } from '@/composables/RulesManager'
import { Rule } from '@/enums/Rule'
import { getAuthInstance } from '@fusion/auth-v3'
import axios from 'axios'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import PrimaryTextField from '../inputs/PrimaryTextField.vue'

const RulesManager = useRulesManager()
const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS
const props = defineProps({
  activationCode: { type: String, default: '' }
})

// REACTIVE VARIABLES
const _activationCode = ref<string>('')
const isFormValid = ref<boolean>(false)
const isValidatingCode = ref<boolean>(false)
const errorMessage = ref<string>()
const activationForm = ref<any>()

// COMPUTED PROPERTIES
const email = computed(() => {
  return `<strong>technical.services@tsi.com</strong>`
})

const instructions = computed(() => {
  if (!props.activationCode || props.activationCode.length === 0) {
    return t('text.content.activateViaChannel', { link: link.value, email: email.value })
  }
  return t('text.content.activateDirect', { link: link.value, email: email.value })
})

const link = computed(() => {
  return `<a target="_blank" href="https://tsi.com/contact-us/locations">${t('labels.here')}</a>`
})

// LIFECYCLE HOOKS
onMounted(async () => {
  // Initialize the activation code if provided
  if (props.activationCode && props.activationCode.length > 0) {
    _activationCode.value = props.activationCode
    // Validate the form initially
    await nextTick()
    await validateForm()
  }
})

// WATCHERS

// FUNCTIONS
const validateForm = async () => {
  errorMessage.value = ''
  const results = await activationForm.value.validate()
  isFormValid.value = results.valid
}

const activate = async () => {
  try {
    isValidatingCode.value = true
    errorMessage.value = ''
    const response = await axios.post(
      `${APP_ENV.VITE_APP_SUBS_HOST}/subscriptions/user/activation/add/${_activationCode.value}`,
      JSON.stringify({})
    )
    if (response.status === 200) {
      const auth0 = getAuthInstance()
      // Force auth0 to reauthenticate so rules are run again and user roles are updated
      await auth0.getTokenSilently({ cacheMode: 'off' })
      // Handle success
      window.location.reload()
      return
    } else if (response.status === 422) {
      // Handle validation error
      /**
       * Use response.data.errors[0].name as a way to specify locale based error messages
        {
            "errors":[
                {"code":90001,"type":"validation","name":"InvalidActivationCodeError","message":"Invalid code"},
                {"code":90007,"type":"validation","name":"AlreadyAddedActivationCodeError","message":"The subscription is already added to your account"},
                {"code":90002,"type":"validation","name":"InUseActivationCodeError","message":"This code has been activated previously"},
                {"code":90003,"type":"validation","name":"ExpiredActivationCodeError","message":"This code has expired"}
            ]
        }
      */
      errorMessage.value = t(`errors.activationErrorResponses.${response.data.errors[0].name}`)
    } else if (response.status === 401) {
      errorMessage.value = t(`errors.activationErrorResponses.UnauthorizedError`)
    } else if (response.data?.name === 'MissingSAPIDError') {
      errorMessage.value = t(`errors.activationErrorResponses.${response.data.name}`)
    } else {
      errorMessage.value = t(`errors.activationErrorResponses.UnknownError`)
    }
  } catch {
    // Just a stock error
    errorMessage.value = t(`errors.activationErrorResponses.UnknownError`)
  }
  isValidatingCode.value = false
}
</script>

<style lang="scss" scoped>
.section {
  padding: 1rem 1rem 0rem 1rem;
  max-width: 500px;
}

.section-content {
  margin: 1rem 0 1rem 0;
  padding: 0;
}

.title {
  margin: 0;
  padding: 0;
}
</style>
